import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  Film,
  Zap,
  Mic,
  AlignLeft,
  Settings,
  Sparkles,
  Clock,
  Video,
  Clapperboard,
  BookOpen,
  Info,
  AlertTriangle,
  CheckCircle,
  Flag
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../../firebase.init";
import { deductUserCredit } from "../../../../utils/geminiUtils";


const DocumentaryScriptGenerator = ({ toolPath = "/user/documentary-script-generator" }) => {
  // --- AUTH & CORE STATE ---
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingTools, setLoadingTools] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // --- FORM STATE ---
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("General Audience");
  const [videoLength, setVideoLength] = useState("1000 words");
  const [docStyle, setDocStyle] = useState("Mystery");
  const [tone, setTone] = useState("Cinematic");
  
  // Toggles
  const [includeHooks, setIncludeHooks] = useState(true);
  const [includeScenes, setIncludeScenes] = useState(true);
  const [includeVoStyle, setIncludeVoStyle] = useState(true);

  // --- OUTPUT STATE ---
  const [scriptData, setScriptData] = useState(null);

  // --- CREDIT & TOOL CONFIG ---
  
  const [creditCost, setCreditCost] = useState(2);
  const [apiKeys, setApiKeys] = useState([]);

  // --- OPTIONS ---
  const audienceOptions = [
    "General Audience",
    "True Crime Fans",
    "History Buffs",
    "Science & Tech Enthusiasts",
    "Mystery Seekers",
    "Entrepreneurs & Business",
    "Pop Culture Fans",
    "Nature & Wildlife Lovers"
  ];
  const lengthOptions = ["250 words", "500 words", "1000 words", "1500 words", "Long (2000+ words)"];
  const styleOptions = ["Historical", "Mystery", "Biography", "Educational", "Investigative", "Motivational Storytelling"];
  const toneOptions = ["Serious", "Emotional", "Cinematic", "Informative", "Dramatic"];

  // Fetch API keys
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) setError("No API keys available. Please contact administrator.");
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Fetch Tools List
  const fetchToolsList = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/tools-section`);
      if (response.data && response.data.success && response.data.data && response.data.data.tools) {
        const tool = response.data.data.tools.find(t => t.id === 7); 
        if (tool) {
            setCreditCost(tool.creditCost);
        }
      }
    } catch (err) {
        console.warn("Could not fetch tools list, using default credit cost");
    } finally {
        setLoadingTools(false);
    }
  };

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // Initial Data Fetch
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        setLoadingTools(false);
        return;
      }
      try {
        await Promise.all([fetchApiKeys(), fetchToolsList()]);
        
        const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Prompt Engineering for Documentary Script
  const constructPrompt = () => {
    return `You are a master documentary filmmaker and scriptwriter for massive YouTube channels. 
    Write a highly engaging, faceless documentary script based on these parameters:

    - Topic: ${topic}
    - Target Audience: ${audience}
    - Target Script Length: ${videoLength}
    - Documentary Style: ${docStyle}
    - Tone: ${tone}
    - Generate Hook Variations: ${includeHooks ? "Yes (Provide 3 variations)" : "No (Provide 1 powerful hook)"}
    - Include Scene/Visual Descriptions: ${includeScenes ? "Yes" : "No"}
    - Voiceover Direction: ${includeVoStyle ? "Yes (Include tone/pacing cues for the narrator)" : "No"}

    RULES:
    1. The narration must be captivating, emotional, and use cinematic tension.
    2. Write in short, spoken sentences suitable for a voiceover artist.
    3. Ensure logical chronological or narrative flow.
    4. Provide detailed, factual storytelling (avoid hallucinations).
    5. Each hook must be 30–45 seconds long when spoken (50–90 words).
    
    CRITICAL INSTRUCTION:
    Respond ONLY with a raw, valid JSON object matching the exact schema below. Do not wrap in markdown \`\`\`json blocks.
    
    JSON SCHEMA REQUIRED:
    {
      "title": "Cinematic Title of the Documentary",
      "hooks": [
        {
          "type": "Curiosity / Emotional / Action",
          "scene": "Visual description (if enabled)",
          "narration": "The spoken hook text..."
        }
      ],
      "introduction": {
        "scene": "Visual description",
        "narration": "Setup the context and background..."
      },
      "storyDevelopment": [
        {
          "heading": "Chapter / Section Name",
          "scene": "Visual description",
          "narration": "The spoken script for this section..."
        }
      ],
      "keyInformation": [
        {
          "heading": "Key Fact or Analysis Section",
          "scene": "Visual description",
          "narration": "The spoken script..."
        }
      ],
      "climax": {
        "scene": "Visual description",
        "narration": "The peak tension or dramatic moment of the story..."
      },
      "conclusion": {
        "scene": "Visual description",
        "narration": "The final message, lesson, or reflection..."
      },
      "cta": {
        "narration": "A natural sign-off encouraging likes/subscriptions..."
      }
    }`;
  };

  // Generate content
  const generateScript = async () => {
    if (!topic.trim()) {
      setError("Please provide a Documentary Topic.");
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }
    

    setGenerating(true);
    setError("");
    setScriptData(null);

    try {
      // Deduct credits
      
      
      

      // Call AI
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 6000, 
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (data.candidates && data.candidates.length > 0 && data.candidates[0].content) {
        let generatedText = data.candidates[0].content.parts[0].text;
        
        // Clean markdown backticks to parse JSON safely
        generatedText = generatedText.replace(/```json/g, '').replace(/```/g, '').trim();
        
        try {
          const parsedContent = JSON.parse(generatedText);
          setScriptData(parsedContent);
          toast.success("Documentary Script generated successfully!");
        } catch (parseErr) {
          throw new Error("AI returned invalid data format. Please try again.");
        }
      } else {
        throw new Error("No content was generated from the API response");
      }
    } catch (err) {
      console.error("Error generating content:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);

      
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`);
    }).catch(() => {
      toast.error(`Failed to copy ${label}`);
    });
  };

  // Helper to build text for the Download All button
  const buildFullScriptText = () => {
    if (!scriptData) return "";
    let fullText = `DOCUMENTARY SCRIPT: ${scriptData.title}\n\n`;
    
    fullText += `--- HOOKS ---\n`;
    scriptData.hooks.forEach((hook, i) => {
      fullText += `Option ${i+1} (${hook.type}):\n`;
      if (includeScenes) fullText += `[VISUAL]: ${hook.scene}\n`;
      fullText += `[VOICEOVER]: ${hook.narration}\n\n`;
    });

    fullText += `--- INTRODUCTION ---\n`;
    if (includeScenes) fullText += `[VISUAL]: ${scriptData.introduction.scene}\n`;
    fullText += `[VOICEOVER]: ${scriptData.introduction.narration}\n\n`;

    fullText += `--- STORY DEVELOPMENT ---\n`;
    scriptData.storyDevelopment.forEach(section => {
      fullText += `## ${section.heading}\n`;
      if (includeScenes) fullText += `[VISUAL]: ${section.scene}\n`;
      fullText += `[VOICEOVER]: ${section.narration}\n\n`;
    });

    if (scriptData.keyInformation && scriptData.keyInformation.length > 0) {
      fullText += `--- KEY INFORMATION ---\n`;
      scriptData.keyInformation.forEach(section => {
        fullText += `## ${section.heading}\n`;
        if (includeScenes) fullText += `[VISUAL]: ${section.scene}\n`;
        fullText += `[VOICEOVER]: ${section.narration}\n\n`;
      });
    }

    fullText += `--- THE CLIMAX ---\n`;
    if (includeScenes) fullText += `[VISUAL]: ${scriptData.climax.scene}\n`;
    fullText += `[VOICEOVER]: ${scriptData.climax.narration}\n\n`;

    fullText += `--- CONCLUSION ---\n`;
    if (includeScenes) fullText += `[VISUAL]: ${scriptData.conclusion.scene}\n`;
    fullText += `[VOICEOVER]: ${scriptData.conclusion.narration}\n\n`;

    fullText += `--- CALL TO ACTION ---\n`;
    fullText += `[VOICEOVER]: ${scriptData.cta.narration}\n`;

    return fullText;
  };

  const downloadContent = () => {
    const text = buildFullScriptText();
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(scriptData.title || "documentary").replace(/[^a-z0-9]/gi, '_').toLowerCase()}-script.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Script downloaded!");
  };

  if (loadingAuth || loading || loadingKeys || loadingTools) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
            <div style={{ border: '4px solid #f1f5f9', borderTop: '4px solid #0f172a', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', margin: '0 auto 10px' }}></div>
            <p style={{ color: '#64748b', fontWeight: '500' }}>Loading Workspace...</p>
        </div>
       
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ background: 'white', padding: '3rem', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)', textAlign: 'center', maxWidth: '400px' }}>
          <Film size={48} color="#0f172a" style={{ marginBottom: '1rem' }} />
          <h2 style={{ color: '#0f172a', marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '700' }}>Authentication Required</h2>
          <button onClick={() => (window.location.href = "/login")} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', width: '100%' }}>
            Proceed to Login
          </button>
        </div>
      </div>
    );
  }

  // --- REUSABLE COMPONENT FOR SCRIPT SECTIONS ---
  const ScriptSection = ({ title, icon: Icon, data, includeScenes, copyLabel }) => {
    let textToCopy = "";
    if (Array.isArray(data)) {
      data.forEach(d => {
        if (d.heading) textToCopy += `## ${d.heading}\n`;
        if (includeScenes && d.scene) textToCopy += `[VISUAL]: ${d.scene}\n`;
        textToCopy += `[VOICEOVER]: ${d.narration}\n\n`;
      });
    } else {
      if (includeScenes && data.scene) textToCopy += `[VISUAL]: ${data.scene}\n`;
      textToCopy += `[VOICEOVER]: ${data.narration}\n`;
    }

    return (
      <div className="script-block">
        <div className="script-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b', fontWeight: '700' }}>
            <Icon size={18} /> {title}
          </div>
          <button className="copy-btn" onClick={() => copyToClipboard(textToCopy.trim(), copyLabel)}>
            <Copy size={14} /> Copy Section
          </button>
        </div>
        <div className="script-body">
          {Array.isArray(data) ? (
            data.map((item, idx) => (
              <div key={idx} style={{ marginBottom: idx !== data.length - 1 ? '1.5rem' : 0 }}>
                {item.heading && <h4 className="section-subheading">{item.heading}</h4>}
                {item.type && <h4 className="section-subheading" style={{ color: '#64748b' }}>Option {idx + 1}: {item.type}</h4>}
                {includeScenes && item.scene && (
                  <div className="visual-cue"><strong>[VISUAL]:</strong> {item.scene}</div>
                )}
                <div className="vo-cue"><strong>[VOICEOVER]:</strong> {item.narration}</div>
              </div>
            ))
          ) : (
            <div>
              {includeScenes && data.scene && (
                <div className="visual-cue"><strong>[VISUAL]:</strong> {data.scene}</div>
              )}
              <div className="vo-cue"><strong>[VOICEOVER]:</strong> {data.narration}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .doc-app { font-family: 'Inter', system-ui, sans-serif; background: #f8fafc; min-height: 100vh; padding: 2rem 1rem; color: #334155; }
        .saas-card { background: #ffffff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.04); border: 1px solid #e2e8f0; padding: 1.75rem; display: flex; flex-direction: column;}
        .form-label { font-weight: 600; color: #475569; font-size: 0.9rem; margin-bottom: 0.5rem; display: block; }
        .form-control, .form-select { width: 100%; padding: 0.85rem 1rem; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 0.95rem; color: #0f172a; transition: all 0.2s; margin-bottom: 1.25rem; box-sizing: border-box; }
        .form-control:focus, .form-select:focus { outline: none; border-color: #0f172a; box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.1); background: #fff; }
        
        /* Updated margin-top to 1rem instead of auto to prevent it from dropping to the bottom */
        .gradient-btn { background: linear-gradient(135deg, #1e293b 0%, #020617 100%); color: white; border: none; padding: 1rem 1.5rem; border-radius: 10px; font-weight: 600; font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.5rem; transition: all 0.3s ease; width: 100%; box-shadow: 0 4px 12px rgba(15, 23, 42, 0.25); margin-top: 1rem;}
        .gradient-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(15, 23, 42, 0.35); }
        .gradient-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }
        
        .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; border-radius: 34px; }
        .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; }
        input:checked + .slider { background-color: #0f172a; }
        input:checked + .slider:before { transform: translateX(20px); }

        .script-block { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 1.5rem; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.02); }
        .script-header { background: #f8fafc; padding: 1rem 1.25rem; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
        .script-body { padding: 1.5rem; }
        
        .section-subheading { font-size: 1.05rem; font-weight: 800; color: #0f172a; margin: 0 0 10px 0; text-transform: uppercase; letter-spacing: 0.02em;}
        .visual-cue { background: #f1f5f9; color: #475569; padding: 12px; border-radius: 6px; font-size: 0.95rem; font-style: italic; margin-bottom: 10px; border-left: 3px solid #94a3b8; }
        .vo-cue { background: #fff; color: #1e293b; padding: 12px; border-radius: 6px; font-size: 1.05rem; line-height: 1.6; border: 1px solid #e2e8f0; border-left: 3px solid #0f172a; font-family: 'Georgia', serif;}
        
        .copy-btn { background: white; border: 1px solid #cbd5e1; color: #475569; padding: 6px 12px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: all 0.2s; }
        .copy-btn:hover { background: #f1f5f9; color: #0f172a; border-color: #94a3b8;}
        
        @keyframes custom-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.98); } }
        .anim-pulse { animation: custom-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>

      <div className="doc-app">
      
        
        <div className="container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Top Navigation */}
          <div className="row mb-4">
            <div className="col-12 d-flex justify-content-between align-items-center" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => window.history.back()} style={{ background: "#fff", border: "1px solid #e2e8f0", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", color: "#64748b" }}>
                ← Dashboard
              </button>
              
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-5" style={{ textAlign: "center", margin: "3rem 0" }}>
            <h1 style={{ fontSize: "2.5rem", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", letterSpacing: "-0.02em" }}>
              <Film color="#0f172a" size={40} /> Faceless Documentary Scripts
            </h1>
            <p style={{ color: "#64748b", marginTop: "12px", fontSize: "1.1rem", maxWidth: "650px", margin: "12px auto 0" }}>
              Generate highly cinematic, deeply researched, and narration-ready documentary scripts for YouTube.
            </p>
          </div>

          {error && (
            <div style={{ background: "#fef2f2", color: "#ef4444", padding: "1rem", borderRadius: "10px", marginBottom: "2rem", textAlign: "center", border: "1px solid #fecaca", fontWeight: "600" }}>
              {error}
            </div>
          )}

          {/* Added alignItems: 'flex-start' here to prevent the left form column from stretching down */}
          <div className="row" style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-start' }}>
            
            {/* Left Column - Form */}
            <div className="col-lg-4 col-md-5" style={{ flex: '1 1 350px', maxWidth: '450px' }}>
              <div className="saas-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #f1f5f9' }}>
                  <Clapperboard size={20} color="#0f172a" />
                  <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a', fontWeight: '700' }}>Directorial Vision</h3>
                </div>
                
                <label className="form-label">Documentary Topic <span style={{color:'#ef4444'}}>*</span></label>
                <textarea
                  className="form-control"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., The unsolved mystery of the Flannan Isles Lighthouse, The rise and fall of Blockbuster..."
                  rows={3}
                />

                <label className="form-label">Target Audience (Optional)</label>
                <select className="form-select" value={audience} onChange={(e) => setAudience(e.target.value)}>
                  {audienceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Video Length</label>
                    <select className="form-select" value={videoLength} onChange={(e) => setVideoLength(e.target.value)}>
                      {lengthOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Doc Style</label>
                    <select className="form-select" value={docStyle} onChange={(e) => setDocStyle(e.target.value)}>
                      {styleOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>

                <label className="form-label">Narration Tone</label>
                <select className="form-select" value={tone} onChange={(e) => setTone(e.target.value)}>
                  {toneOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>

                <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '12px', marginTop: '0.5rem', marginBottom: '1.5rem', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>Visual/Scene Directions</span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Generate b-roll ideas</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includeScenes} onChange={(e) => setIncludeScenes(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>Hook Variations</span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Provide 3 intro hooks</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includeHooks} onChange={(e) => setIncludeHooks(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>Voiceover Directives</span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Include pacing cues</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includeVoStyle} onChange={(e) => setIncludeVoStyle(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                </div>

                <button onClick={generateScript} disabled={generating || !topic.trim() } className="gradient-btn">
                  {generating ? <Clock size={18} className="fa-spin" /> : <Sparkles size={18} />}
                  {generating ? "Writing Script..." : `Generate Documentary `}
                </button>
              </div>
            </div>

            {/* Right Column - Results */}
            <div className="col-lg-8 col-md-7" style={{ flex: '1 1 500px' }}>
              
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <h2 style={{ margin: 0, fontSize: "1.5rem", color: "#0f172a", fontWeight: "700" }}>Generated Script</h2>
                {scriptData && (
                  <button onClick={downloadContent} style={{ background: "#0f172a", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "0.9rem", fontWeight: "600", boxShadow: "0 2px 4px rgba(15, 23, 42, 0.2)" }}>
                    <Download size={16} /> Download Full Script
                  </button>
                )}
              </div>

              {generating ? (
                 <div className="saas-card anim-pulse" style={{ textAlign: "center", padding: "6rem 2rem", display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <div style={{ width: '70px', height: '70px', background: '#f1f5f9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                      <Video size={34} color="#0f172a" />
                    </div>
                    <h3 style={{ color: '#0f172a', fontWeight: '800', marginBottom: '0.5rem' }}>Directing the Narrative...</h3>
                    <p style={{ color: "#64748b", maxWidth: "350px", margin: "0 auto" }}>Structuring chapters, writing cinematic voiceovers, and planning visual b-roll sequences.</p>
                 </div>
              ) : scriptData ? (
                <div>
                  
                  {/* Master Title */}
                  <div style={{ background: '#0f172a', color: 'white', padding: '1.5rem', borderRadius: '12px', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Documentary Title</span>
                      <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800' }}>{scriptData.title}</h2>
                    </div>
                    <button onClick={() => copyToClipboard(scriptData.title, "Title")} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', display: 'flex', gap: '6px', fontSize: '0.85rem' }}>
                      <Copy size={16} /> Copy
                    </button>
                  </div>

                  {/* Components */}
                  <ScriptSection 
                    title="The Hooks (0:00 - 0:30)" 
                    icon={Zap} 
                    data={scriptData.hooks} 
                    includeScenes={includeScenes} 
                    copyLabel="Hooks" 
                  />

                  <ScriptSection 
                    title="Introduction & Setup" 
                    icon={BookOpen} 
                    data={scriptData.introduction} 
                    includeScenes={includeScenes} 
                    copyLabel="Introduction" 
                  />

                  <ScriptSection 
                    title="Story Development Chapters" 
                    icon={Film} 
                    data={scriptData.storyDevelopment} 
                    includeScenes={includeScenes} 
                    copyLabel="Story Chapters" 
                  />

                  {scriptData.keyInformation && scriptData.keyInformation.length > 0 && (
                    <ScriptSection 
                      title="Key Facts & Analysis" 
                      icon={Info} 
                      data={scriptData.keyInformation} 
                      includeScenes={includeScenes} 
                      copyLabel="Key Info" 
                    />
                  )}

                  <ScriptSection 
                    title="The Climax / Dramatic Peak" 
                    icon={AlertTriangle} 
                    data={scriptData.climax} 
                    includeScenes={includeScenes} 
                    copyLabel="Climax" 
                  />

                  <ScriptSection 
                    title="Conclusion & Reflection" 
                    icon={CheckCircle} 
                    data={scriptData.conclusion} 
                    includeScenes={includeScenes} 
                    copyLabel="Conclusion" 
                  />

                  <ScriptSection 
                    title="Call to Action (Sign-off)" 
                    icon={Flag} 
                    data={scriptData.cta} 
                    includeScenes={false} // CTAs usually don't need complex scenes
                    copyLabel="CTA" 
                  />

                </div>
              ) : (
                <div className="saas-card" style={{ textAlign: 'center', padding: '6rem 2rem', border: '2px dashed #cbd5e1', background: 'transparent', boxShadow: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Clapperboard size={56} color="#cbd5e1" style={{ marginBottom: '1.5rem' }} />
                  <h3 style={{ color: '#64748b', fontWeight: '700', margin: '0 0 0.5rem 0' }}>Ready to Create</h3>
                  <p style={{ color: '#94a3b8', margin: 0, maxWidth: '350px' }}>Enter your documentary topic on the left to generate a deeply researched, cinematic script.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentaryScriptGenerator;